/**
 * Minimal CSV reader/writer for the bulk invite import.
 *
 * Hand-rolled rather than pulling in a parser: the only file this reads is one
 * we also supply the template for, and the format it needs to survive is
 * whatever Excel and Google Sheets emit — quoted fields, commas and newlines
 * inside quotes, doubled quotes as an escape, and CRLF line endings.
 */

/** Split a CSV document into rows of raw cell strings. A tab delimiter reads TSV. */
export function parseCsv(text: string, delimiter = ','): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  // Excel writes a UTF-8 BOM; left in place it becomes part of the first header.
  const input = text.replace(/^﻿/, '');

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          cell += '"'; // escaped quote
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      // Consume the \n of a \r\n pair so the line does not split twice.
      if (char === '\r' && input[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  // Whatever is left when the file does not end in a newline.
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  // Drop rows that are entirely empty — trailing newlines are common.
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/**
 * Parse into objects keyed by header. Headers are matched case-insensitively
 * and ignoring spaces/underscores, so "Employee ID", "employee_id" and
 * "employeeId" all land on the same field.
 */
export function parseCsvToObjects(
  text: string,
  fieldAliases: Record<string, string>
): { rows: Record<string, string>[]; headers: string[]; unknownHeaders: string[] } {
  const raw = parseCsv(text);
  if (raw.length === 0) return { rows: [], headers: [], unknownHeaders: [] };

  const normalise = (h: string) => h.trim().toLowerCase().replace(/[\s_-]/g, '');
  const headers = raw[0].map((h) => h.trim());
  const unknownHeaders: string[] = [];

  const mapped = headers.map((h) => {
    const key = fieldAliases[normalise(h)];
    if (!key) unknownHeaders.push(h);
    return key ?? null;
  });

  const rows = raw.slice(1).map((cells) => {
    const obj: Record<string, string> = {};
    mapped.forEach((key, idx) => {
      if (key) obj[key] = (cells[idx] ?? '').trim();
    });
    return obj;
  });

  return { rows, headers, unknownHeaders };
}

/** Serialise rows to CSV, quoting only what needs it. */
export function toCsv(headers: string[], rows: (string | number | null | undefined)[][]): string {
  const escape = (value: string | number | null | undefined) => {
    const s = value == null ? '' : String(value);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers, ...rows].map((r) => r.map(escape).join(',')).join('\r\n');
}

/** Trigger a browser download of a generated CSV. */
export function downloadCsv(filename: string, csv: string): void {
  // The BOM makes Excel open UTF-8 correctly instead of mangling accents.
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
